/**
 * 스마트 학습 네비게이션 유틸리티
 * 사용자의 학습 진도에 맞춰 최적의 학습 경로를 추천
 */

import { getLearningProgress } from './learningProgressStorage';
import { getWrongAnswers } from './wrongAnswerStorage';
import { getKoreanExamName } from './examNameMapping';

// 사용 가능한 시험 데이터 매핑 (실제 파일 존재 여부 기준)
const AVAILABLE_EXAM_DATA = {
  'electrical-engineer': [
    { year: 2022, sessions: [1, 2] },
    { year: 2023, sessions: [1, 2, 3] },
    { year: 2024, sessions: [1, 2, 3] }
  ],
  'electrical-industrial-engineer': [
    { year: 2022, sessions: [1, 2, 3] },
    { year: 2023, sessions: [1, 2, 3] },
    { year: 2024, sessions: [1, 2, 3] }
  ],
  'information-processing-engineer': [
    { year: 2020, sessions: [2, 3, 4] },
    { year: 2021, sessions: [1, 2, 3] },
    { year: 2022, sessions: [1, 2] }
  ],
  'computer-applications-level1': [
    { year: 2022, sessions: [1, 2, 3, 4] },
    { year: 2023, sessions: [1, 2, 3, 4] },
    { year: 2024, sessions: [1, 2, 3, 4] }
  ],
  'computer-applications-level2': [
    { year: 2022, sessions: [1, 2, 3, 4] },
    { year: 2023, sessions: [1, 2, 3, 4] },
    { year: 2024, sessions: [1, 2, 3, 4] }
  ]
};

// 주어진 examType에 대해 사용 가능한 첫 번째 세션 찾기
const getFirstAvailableSession = (examType) => {
  const availableData = AVAILABLE_EXAM_DATA[examType];
  if (!availableData || availableData.length === 0) {
    // 기본값
    return { year: 2023, session: 1 };
  }
  
  // 가장 최근 년도의 첫 번째 세션 선택
  const latestYearData = availableData[availableData.length - 1];
  const firstSession = Math.min(...latestYearData.sessions);
  
  return {
    year: latestYearData.year,
    session: firstSession
  };
};

// 추천 시험 및 문제 번호 계산
export const getRecommendedStudyPath = (userId) => {
  if (!userId) {
    // 게스트 사용자의 경우 기본 추천 - 사용 가능한 데이터 사용
    const defaultExamType = 'information-processing-engineer';
    const { year, session } = getFirstAvailableSession(defaultExamType);
    
    return {
      recommendationType: 'default',
      examType: defaultExamType,
      year: year,
      session: session,
      startQuestionNumber: 1,
      reason: '첫 학습을 시작해보세요'
    };
  }

  const progressData = getLearningProgress(userId);
  const wrongAnswers = getWrongAnswers(userId);

  // 학습 진도 데이터가 없는 경우 - 사용 가능한 데이터 사용
  if (!progressData) {
    const defaultExamType = 'information-processing-engineer';
    const { year, session } = getFirstAvailableSession(defaultExamType);
    
    return {
      recommendationType: 'firstTime',
      examType: defaultExamType,
      year: year,
      session: session,
      startQuestionNumber: 1,
      reason: '첫 학습을 시작해보세요'
    };
  }

  // 1순위: 가장 최근에 학습한 시험의 다음 부분 (진단평가 완료 후 연결 우선)
  if (progressData.examProgress && Object.keys(progressData.examProgress).length > 0) {
    const examEntries = Object.entries(progressData.examProgress)
      .sort(([,a], [,b]) => new Date(b.lastStudied) - new Date(a.lastStudied));

    const [mostRecentExamKey, examData] = examEntries[0];
    
    // 진단평가인지 확인
    if (mostRecentExamKey.startsWith('diagnostic_')) {
      const diagnosticExamType = mostRecentExamKey.replace('diagnostic_', '');
      
      // 진단평가가 완료되었다면 해당 시험의 일반 문제로 연결
      if (examData.progress >= 100 || examData.completed) {
        // 사용 가능한 첫 번째 세션 찾기
        const { year, session } = getFirstAvailableSession(diagnosticExamType);
        
        return {
          recommendationType: 'afterDiagnostic',
          examType: diagnosticExamType,
          year: year,
          session: session,
          startQuestionNumber: 1,
          reason: `${getKoreanExamName(diagnosticExamType)} 진단평가를 완료했습니다`,
          targetCertification: getKoreanExamName(diagnosticExamType)
        };
      } else {
        // 진단평가가 진행중이라면 일반 문제풀이로 이동 (진단평가는 이어서 풀 수 없음)
        const { year, session } = getFirstAvailableSession(diagnosticExamType);
        
        return {
          recommendationType: 'afterDiagnostic', 
          examType: diagnosticExamType,
          year: year,
          session: session,
          startQuestionNumber: 1,
          reason: `${getKoreanExamName(diagnosticExamType)} 문제에 도전해보세요`,
          targetCertification: getKoreanExamName(diagnosticExamType)
        };
      }
    }
    
    // 일반 시험인 경우
    const [examType, year, session] = mostRecentExamKey.split('_');

    // 진행률이 100%가 아니라면 이어서 학습
    if (examData.progress < 100) {
      let nextQuestionNumber = 1;
      
      // 실제로 푼 문제 번호들이 있다면 가장 큰 번호 + 1
      if (examData.solvedQuestions && examData.solvedQuestions.length > 0) {
        const maxSolvedQuestion = Math.max(...examData.solvedQuestions);
        nextQuestionNumber = maxSolvedQuestion + 1;
      } else {
        // 기존 로직 (진도율 기준)
        const totalQuestions = examData.totalQuestions || 100;
        nextQuestionNumber = Math.max(1, Math.floor((examData.progress / 100) * totalQuestions) + 1);
      }
      
      return {
        recommendationType: 'continue',
        examType,
        year: parseInt(year),
        session: parseInt(session),
        startQuestionNumber: nextQuestionNumber,
        reason: `${getKoreanExamName(examType)} ${Math.round(examData.progress)}% 진행중`,
        targetCertification: getKoreanExamName(examType)
      };
    }

    // 완료한 시험이라면 다음 세션 추천
    const nextSession = parseInt(session) + 1;
    if (nextSession <= 4) { // 일반적으로 세션은 1-4회
      return {
        recommendationType: 'nextSession',
        examType,
        year: parseInt(year),
        session: nextSession,
        startQuestionNumber: 1,
        reason: `${session}회를 완료했습니다. ${nextSession}회에 도전해보세요`
      };
    }

    // 다음 연도 추천
    const nextYear = parseInt(year) + 1;
    const currentYear = new Date().getFullYear();
    if (nextYear <= currentYear) {
      return {
        recommendationType: 'nextYear',
        examType,
        year: nextYear,
        session: 1,
        startQuestionNumber: 1,
        reason: `${year}년을 완료했습니다. ${nextYear}년에 도전해보세요`
      };
    }
  }

  // 2순위: 틀린 문제가 많다면 오답 복습 추천
  if (wrongAnswers && wrongAnswers.length >= 5) {
    const recentWrongAnswers = wrongAnswers
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 10);
    
    // 가장 많이 틀린 시험 유형 찾기
    const wrongByExam = recentWrongAnswers.reduce((acc, item) => {
      const key = `${item.examType}_${item.year || 2023}_${item.session || 1}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const mostProblematicExam = Object.entries(wrongByExam)
      .sort(([,a], [,b]) => b - a)[0];

    if (mostProblematicExam) {
      const [examKey, count] = mostProblematicExam;
      const [examType, year, session] = examKey.split('_');
      
      return {
        recommendationType: 'wrongAnswerReview',
        examType,
        year: parseInt(year),
        session: parseInt(session),
        startQuestionNumber: 1,
        reason: `${getKoreanExamName(examType)} ${count}개 오답 복습`,
        targetCertification: getKoreanExamName(examType)
      };
    }
  }

  // 3순위: 과목별 약점 보완
  if (progressData.subjectProgress && Object.keys(progressData.subjectProgress).length > 0) {
    const subjects = Object.entries(progressData.subjectProgress)
      .filter(([, data]) => data.accuracy < 70) // 정확도 70% 미만
      .sort(([,a], [,b]) => a.accuracy - b.accuracy); // 정확도 낮은 순

    if (subjects.length > 0) {
      const [weakestSubject] = subjects[0];
      const defaultExamType = 'information-processing-engineer';
      const { year, session } = getFirstAvailableSession(defaultExamType);
      
      return {
        recommendationType: 'subjectWeakness',
        examType: defaultExamType,
        year: year,
        session: session,
        startQuestionNumber: 1,
        reason: `${weakestSubject} 과목 보완이 필요합니다`,
        focusSubject: weakestSubject
      };
    }
  }

  // 4순위: 목표 기반 추천
  if (progressData.goals && progressData.goals.targetExamDate) {
    const targetDate = new Date(progressData.goals.targetExamDate);
    const today = new Date();
    const daysLeft = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysLeft > 0 && daysLeft < 30) {
      const defaultExamType = progressData.currentCertification === '정보처리기사' ? 'information-processing-engineer' : 'information-processing-engineer';
      const { year, session } = getFirstAvailableSession(defaultExamType);
      
      return {
        recommendationType: 'examPrep',
        examType: defaultExamType,
        year: year,
        session: session,
        startQuestionNumber: 1,
        reason: `시험까지 ${daysLeft}일 남았습니다. 최신 문제로 연습하세요`
      };
    }
  }

  // 기본 추천 (새로운 학습자) - 사용 가능한 데이터 사용
  const defaultExamType = 'information-processing-engineer';
  const { year, session } = getFirstAvailableSession(defaultExamType);
  
  return {
    recommendationType: 'default',
    examType: defaultExamType,
    year: year,
    session: session,
    startQuestionNumber: 1,
    reason: '새로운 학습을 시작해보세요'
  };
};

// 추천 데이터를 기반으로 CBT 시작 URL 생성
export const getRecommendedExamUrl = (userId) => {
  const recommendation = getRecommendedStudyPath(userId);
  
  return {
    ...recommendation,
    url: `/exam/${recommendation.examType}/${recommendation.year}/${recommendation.session}?start=${recommendation.startQuestionNumber}`
  };
};

// 학습 패턴 분석
export const analyzeStudyPattern = (userId) => {
  const progressData = getLearningProgress(userId);
  if (!progressData || !progressData.studyPattern) {
    return {
      preferredTime: 'morning',
      avgSessionLength: 30,
      consistency: 'beginner'
    };
  }

  const { studyPattern } = progressData;
  
  return {
    preferredTime: studyPattern.preferredStudyTime || 'evening',
    avgSessionLength: studyPattern.averageSessionTime || 30,
    consistency: studyPattern.consecutiveStudyDays >= 7 ? 'excellent' : 
                studyPattern.consecutiveStudyDays >= 3 ? 'good' : 'needs_improvement',
    totalStudyDays: studyPattern.totalStudyDays || 0,
    currentStreak: studyPattern.consecutiveStudyDays || 0
  };
};

// 추천 이유를 더 자세히 설명
export const getDetailedRecommendationReason = (userId) => {
  const recommendation = getRecommendedStudyPath(userId);
  const pattern = analyzeStudyPattern(userId);
  
  const reasons = [];
  
  switch (recommendation.recommendationType) {
    case 'wrongAnswerReview':
      reasons.push('❌ 틀린 문제가 누적되어 복습이 필요합니다');
      reasons.push('🔄 오답 노트로 약점을 보완하세요');
      break;
      
    case 'continue':
      reasons.push('📈 진행 중인 시험을 완료하는 것이 효과적입니다');
      reasons.push('⚡ 학습 흐름을 유지하세요');
      break;
      
    case 'afterDiagnostic':
      reasons.push('✅ 진단평가를 완료했습니다');
      reasons.push('🎯 이제 실제 문제로 실력을 키워보세요');
      break;
      
    case 'continueDiagnostic':
      reasons.push('📋 진단평가를 진행 중입니다');
      reasons.push('📊 완료하면 맞춤 학습 계획을 제공해드려요');
      break;
      
    case 'nextSession':
    case 'nextYear':
      reasons.push('🎉 이전 단계를 완료했습니다');
      reasons.push('🚀 다음 단계로 도전해보세요');
      break;
      
    case 'subjectWeakness':
      reasons.push(`📚 ${recommendation.focusSubject || '특정'} 과목의 보완이 필요합니다`);
      reasons.push('💪 약점을 극복하면 전체 점수가 향상됩니다');
      break;
      
    case 'examPrep':
      reasons.push('⏰ 시험 날짜가 다가오고 있습니다');
      reasons.push('🎯 실전 대비 연습이 중요합니다');
      break;
      
    default:
      reasons.push('🌟 새로운 학습 여정을 시작해보세요');
      reasons.push('📖 체계적인 학습으로 목표를 달성하세요');
  }
  
  // 학습 패턴에 따른 추가 조언
  if (pattern.consistency === 'excellent') {
    reasons.push('🔥 훌륭한 학습 습관을 유지하고 있습니다');
  } else if (pattern.consistency === 'needs_improvement') {
    reasons.push('📅 꾸준한 학습 습관을 만들어보세요');
  }
  
  return reasons;
};